import {
  ApolloClient,
  FetchResult,
  NextLink,
  NormalizedCacheObject,
  Observable,
  Operation,
} from "@apollo/client";
import { serialize, parse } from "cookie";
import { encrypt } from "./encryption";
import {
  AuthRefreshDocument,
  AuthRefreshMutation,
  AuthRefreshMutationVariables,
  AuthenticationSource,
} from "@gql/generated/graphql";
import { getRoute } from "@config/routes";
import { FlagsmithFeatureType } from "@config/flags";
import { AxiosError } from "axios";

export function storeCompanyId(companyId: string) {
  const companyIdCookie = serialize("companyId", companyId, {
    domain: window.location.hostname,
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
    secure: true,
    sameSite: "lax",
  });
  document.cookie = companyIdCookie;
}

/**
 * As it is a cookie it is present on the server, just not under "document.cookie"
 */
export function getCompanyId(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const { companyId } = parse(document.cookie);

  if (companyId === "undefined") {
    return null;
  }

  return companyId || null;
}

/**
 *
 * @param companyIds
 */
export function storeCompanyIds(companyIds: string[] | "ALL") {
  if (typeof window === "undefined") return;

  let content;
  if (typeof companyIds === "string") {
    content = companyIds;
  } else {
    content = JSON.stringify(companyIds);
  }

  const companyIdsCookie = serialize("companyIds", encrypt(content), {
    domain: window.location.hostname,
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
    secure: true,
    sameSite: "lax",
  });
  document.cookie = companyIdsCookie;
}

/**
 * As it is a cookie it is present on the server, just not under "document.cookie"
 */
export function destroyCompanyIds() {
  if (typeof window === "undefined") return;

  if (typeof document === "undefined") {
    return null;
  }
  const companyIdsCookie = serialize("companyIds", "", {
    domain: window.location.hostname,
    path: "/",
    maxAge: 0,
  });
  const companyExternalIdCookie = serialize("companyExternalId", "", {
    domain: window.location.hostname,
    path: "/",
    maxAge: 0,
  });
  const companyProfileIdCookie = serialize("companyProfileId", "", {
    domain: window.location.hostname,
    path: "/",
    maxAge: 0,
  });

  document.cookie = companyIdsCookie;
  document.cookie = companyExternalIdCookie;
  document.cookie = companyProfileIdCookie;
}

/**
 * As it is a cookie it is present on the server, just not under "document.cookie"
 */
export function destroyCompanyId() {
  if (typeof document === "undefined") {
    return null;
  }
  const companyIdCookie = serialize("companyId", "", {
    domain: window.location.hostname,
    path: "/",
    maxAge: 0,
  });
  document.cookie = companyIdCookie;
}

export const fetchNewTokenByRefreshToken = async (
  client: ApolloClient<NormalizedCacheObject>
) => {
  const newAuth =
    sessionStorage.getItem(FlagsmithFeatureType.NEW_AUTH) === "true";

  if (newAuth) {
    const { postAuthRefreshToken } = await import(
      "@rest/generated/auth/auth/auth"
    );
    const result = await postAuthRefreshToken({ source: "EDASH" });

    return result.tokens.access.token ?? false;
  } else {
    const { data } = await client.mutate<
      AuthRefreshMutation,
      AuthRefreshMutationVariables
    >({
      mutation: AuthRefreshDocument,
      variables: {
        source: AuthenticationSource.Admin,
      },
    });

    return data?.authRefresh ?? false;
  }
};

let refreshPromise: Promise<void> | null = null;

export function fetchNewTokenAndRetry(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  operation: Operation,
  forward: NextLink
) {
  return new Observable<FetchResult>((observer) => {
    const fetchNewTokenSuccess = () => {
      const subscriber = {
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
      };

      return forward(operation).subscribe(subscriber);
    };

    const fetchNewTokenError = (error: unknown) => {
      const currentPath = window.location.pathname;
      const loginPath = getRoute("login");

      if (currentPath !== loginPath) {
        console.error("Failed to refresh tokens, redirecting to login page");
        window.location.replace(loginPath);
      }

      observer.error(error);
      console.error("Failed to refresh tokens");
      console.error(error);
    };

    if (!refreshPromise) {
      refreshPromise = fetchNewTokenByRefreshToken(apolloClient)
        .then(() => {
          refreshPromise = null;
          fetchNewTokenSuccess();
        })
        .catch((error) => {
          refreshPromise = null;
          fetchNewTokenError(error);
        });
    } else {
      refreshPromise.then(fetchNewTokenSuccess).catch(fetchNewTokenError);
    }
  });
}

export const parseAxiosError = (e: AxiosError) => {
  const responseData = e.response?.data as any;

  const errorMessage =
    responseData?.lokaliseKey ||
    responseData?.message ||
    e.message ||
    "Unknown error";

  const message =
    typeof responseData === "object" &&
    responseData &&
    "message" in responseData
      ? responseData.message
      : e.message;

  return {
    errorMessage,
    errorName: responseData?.name || "no error name",
    code: e.response?.status || responseData?.code || "no code",
    message: message,
  };
};
